<script lang="ts">
    import dayjs from 'dayjs'
    import type { Schedule } from '../../../modules/schedulerApi/schedules/schedule'
    import type { Appointment } from '../../../modules/schedulerApi/appointments/appointment'
    import { createEventDispatcher, onMount } from 'svelte'
    import AppointmentView from './appointment-view.svelte'
    import ScheduleColumnHeader from './schedule-column-header.svelte'
    import Fa from 'svelte-fa'
    import { faClipboard } from '@fortawesome/free-solid-svg-icons'
    import { AppointmentStatus } from '../../../modules/schedulerApi/appointments/appointments.enum'
    const dispatch = createEventDispatcher()

    export let heightPerMinute: number
    export let schedules: Schedule[] = []
    export let groups: { scheduleGroupId: string; name: string }[] = []
    // scheduleId → "Largura padrão" da agenda (1x/2x). o container só preenche na visão diária; o CSS só aplica no computador
    export let widthFactors: Record<string, number> = {}

    let rows: {
        label: string
        start: number
        end: number
        head: boolean
        cols: colI[]
    }[] = []

    interface colI {
        id: string
        blocked: boolean
        blockId: string
        selected: boolean
        scheduleId: string
        appointments: Appointment[]
        date: Date
    }

    function load(schedules: Schedule[] = []) {
        // ajusta a data do appointment
        for (const schedule of schedules) {
            for (let appointment of schedule.appointments) {
                appointment.start = new Date(appointment.start)
                appointment.end = new Date(appointment.end)
            }
        }
        let range = getSmallestRange()
        let slots = getTotalSlots(range)
        rows = []
        let colId = 0
        for (let index = 0; index < slots.total; index++) {
            let row = {
                cols: [],
                start: slots.start + index * range,
                end: slots.start + (index + 1) * range,
                label: dayjs(DatePlusMinutes(slots.start + index * range)).format('HH:mm'),
                head: (slots.start + index * range) % 60 === 0,
            }
            rows.push(row)
            for (const schedule of schedules) {
                const checkBlock = checkIfSlotIsBlocked(row, schedule)
                let col: colI = {
                    id: 'col_' + colId,
                    blockId: checkBlock.blockId,
                    blocked: checkBlock.blocked,
                    selected: false,
                    appointments: [],
                    scheduleId: schedule.scheduleId,
                    date: DatePlusMinutes(slots.start + index * range, schedule),
                }
                colId++
                row.cols.push(col)
                for (let appointment of schedule.appointments) {
                    const startMinutes = appointment.start.getHours() * 60 + appointment.start.getMinutes()
                    const colMinutes = col.date.getHours() * 60 + col.date.getMinutes()
                    if (startMinutes >= colMinutes && startMinutes < colMinutes + range) {
                        col.appointments.push(appointment)
                    }
                }
            }
        }
    }

    function getNextScheduleSlot(s: Schedule, startTime: number) {
        if (!s.selectedDate || !s.slots) {
            return
        }
        const currentDay = s.slots[s.selectedDate.getDay()]
        const currentSlot = currentDay.slots.find((el) => el.start === startTime)
        return currentSlot
    }

    function checkIfSlotIsBlocked(row, schedule: Schedule) {
        if (row.start < schedule.startMinute) return { blockId: null, blocked: true }
        const currentSlot = getNextScheduleSlot(schedule, row.start)
        if (currentSlot && !currentSlot.active) {
            return { blockId: null, blocked: true }
        }
        for (const appointment of schedule.appointments) {
            if (appointment.status !== AppointmentStatus.agendamento_bloqueado) continue
            const startMinutes = appointment.start.getHours() * 60 + appointment.start.getMinutes()
            const endMinutes = appointment.end.getHours() * 60 + appointment.end.getMinutes()
            if (startMinutes < row.end && endMinutes > row.start) return { blockId: appointment.appointmentId, blocked: true }
            if (startMinutes >= row.start && startMinutes < row.end) return { blockId: appointment.appointmentId, blocked: true }
        }
        return { blockId: null, blocked: false }
    }

    function DatePlusMinutes(minutes: number, schedule: Schedule = null) {
        let dateStart = new Date()
        if (schedule?.selectedDate) dateStart = new Date(schedule.selectedDate)
        dateStart.setHours(0)
        dateStart.setSeconds(0)
        dateStart.setMinutes(minutes)
        return dateStart
    }

    function getSmallestRange() {
        let range = 60
        for (const schedule of schedules) {
            if (schedule.range < range) range = schedule.range
        }
        return range
    }

    function getTotalSlots(range: number) {
        let start = 480
        let end = 1140
        for (const schedule of schedules) {
            if (schedule.startMinute < start) start = schedule.startMinute
            if (schedule.endMinute > end) end = schedule.endMinute
        }
        let total = (end - start) / range
        return {
            start,
            end,
            total,
        }
    }

    function editAppointment(ev) {
        dispatch('edit', ev.detail)
    }
    function editBlock(ev) {
        dispatch('edit-block', ev.detail)
    }

    function onInvoice(event) {
        dispatch('invoice', event.detail)
    }

    let pasting = false
    async function goPaste(start: Date, scheduleId: string) {
        pasting = false
        dispatch('paste', { start, scheduleId })
    }

    function clearSelection() {
        for (const row of rows) {
            for (const col of row.cols) {
                col.selected = false
            }
        }
    }

    function onMouseDown(col: colI) {
        //dispatch('startSelecting', slot)
        clearSelection()
        col.selected = true
        if (window.$.copy) {
            pasting = true
        } else {
            pasting = false
        }
        rows = rows
    }

    function onMouseUp(row, col: colI) {
        dispatch('stopSelecting', {
            start: col.date,
            end: col.date,
            scheduleId: col.scheduleId,
            auto: true, //o tempo de agendamento será calculado automaticamente (auto=false apenas quando se permite selecionar um range de tempo)
        })
    }

    let over = false
    function onDragOver(ev, row, col: colI) {
        if (col.blocked) return
        over = true
        ev.preventDefault()
    }
    function onDragLeave(ev) {
        over = false
    }
    function onDrop(ev, row, col: colI) {
        if (col.blocked) return
        over = false
        goPaste(col.date, col.scheduleId)
        console.log('drop')
    }

    function evaluateColClick(col: colI) {
        if (col.blockId) {
            for (const schedule of schedules) {
                for (let appointment of schedule.appointments) {
                    if (appointment.appointmentId === col.blockId) {
                        dispatch('edit-block', appointment)
                        return
                    }
                }
            }
        }
        dispatch('click')
    }

    onMount(() => {})

    $: load(schedules)
</script>

<div class="schedule-list">
    <div class="-row -header">
        <div class="col -time-header list-header"></div>
        {#each schedules as schedule}
            <!-- mesmo dimensionamento das células da agenda (.-col), pra o cabeçalho acompanhar uma coluna 2x -->
            <div class="-head-col" class:-width-2={widthFactors[schedule.scheduleId] === 2}>
                <ScheduleColumnHeader {schedule} {groups} />
            </div>
        {/each}
    </div>
    {#each rows as row (row.label)}
        <div class="-row">
            <div class="-col -time" class:-is-hour={row.head}>{row.label}</div>
            {#each row.cols as col (col.id)}
                <div
                    class="-col -app"
                    class:-width-2={widthFactors[col.scheduleId] === 2}
                    class:-is-blocked={col.blocked && !col.blockId}
                    class:-is-blocked-editable={!!col.blockId}
                    class:selected={col.selected}
                    class:-is-hour={row.head}
                    on:mousedown={() => onMouseDown(col)}
                    on:mouseup={() => onMouseUp(row, col)}
                    on:dragover={(ev) => onDragOver(ev, row, col)}
                    on:dragleave={(ev) => onDragLeave(ev)}
                    on:drop={(ev) => onDrop(ev, row, col)}
                    on:click|stopPropagation={() => evaluateColClick(col)}
                >
                    {#if pasting && col.selected}
                        <div class="act">
                            <button title="Colar agendamento" on:mousedown|stopPropagation on:click|stopPropagation={() => goPaste(col.date, col.scheduleId)}>
                                <Fa icon={faClipboard} />
                            </button>
                        </div>
                    {/if}
                    {#each col.appointments as appointment}
                        <AppointmentView
                            {heightPerMinute}
                            list={true}
                            item={{ position: 1, shared: 1, appointment: appointment }}
                            on:edit={editAppointment}
                            on:edit-block={editBlock}
                            on:invoice={onInvoice}
                        ></AppointmentView>
                    {/each}
                </div>
            {/each}
        </div>
    {/each}
</div>

<style>
    .schedule-list {
        width: fit-content;
        flex-grow: 1;
        background-color: #383838;
        display: block;
        height: fit-content;
        position: relative;
    }

    .schedule-list .-row {
        display: flex;
        width: 100%;

        /* border-top:1px solid #11111130; */
    }
    .schedule-list .-row.-header {
        position: sticky;
        /* no mobile gruda abaixo da parte visível dos controls da agenda (--sticky-top, definido em
           schedules-container.svelte); no desktop a variável não existe e fica 0 */
        top: var(--sticky-top, 0px);
        /* mesmo motivo do cabeçalho em schedule-column-header.svelte: acima do bloco expandido (3),
           abaixo do menu do Zdrop (5) */
        z-index: 4;
        overflow: hidden;
        font-size: 14px;
    }

    .schedule-list .-row .-col {
        flex: 1 1 165px;
        max-width: 550px;
        min-width: 165px;
        min-height: 80px;
        border-left: 1px solid #11111130;
        padding-bottom: 24px;
        position: relative;
        border-top: 1px solid #11111130;
    }
    .schedule-list .-row .-col.-is-hour {
        border-top: 1px solid #11111188 !important;
    }

    .schedule-list .-row .-head-col {
        flex: 1 1 165px;
        max-width: 550px;
        min-width: 165px;
        display: flex;
    }

    /* agenda com "Largura padrão" 2x: o dobro da base, do máximo e do mínimo — cabeçalho e células juntos.
       só no computador */
    @media (min-width: 901px) {
        .schedule-list .-row .-col.-width-2,
        .schedule-list .-row .-head-col.-width-2 {
            flex: 2 2 330px;
            max-width: 1100px;
            min-width: 330px;
        }
    }

    .schedule-list .-row .-col.-app:hover {
        background-color: #11111110;
    }

    /* mesmo tamanho do horário exibido dentro do agendamento (.ap-item_time) */
    .schedule-list .-row .-time {
        flex: 0 0 45px;
        opacity: 0.8;
        font-size: 12px;
        text-align: right;
        padding-right: 4px;
        min-width: unset;
    }
    /* célula vazia acima da régua de horas: acompanha o fundo da faixa de cabeçalho das agendas */
    .schedule-list .-row .-time-header {
        flex: 0 0 45px;
        background: #333;
        border-bottom: 1px solid #2a2a2a;
    }
    /* mesma hachura de horário indisponível da visão Blocos (.agendamento_desativado) */
    .schedule-list .-row .-is-blocked {
        background: #353535 linear-gradient(135deg, #2e2e2e 25%, #353535 25%, #353535 50%, #2e2e2e 50%, #2e2e2e 75%, #353535 75%, #353535);
        background-size: 17px 17px;
        border-top-color: #00000000 !important;
    }
    .schedule-list .-row .-is-blocked-editable {
        color: #999;
        background: repeating-linear-gradient(90deg, #3b3b3b, #3b3b3b 10px, #404040 10px, #404040 20px);
        border-top-color: #00000000 !important;
        cursor: pointer;
    }
    /* ===== */

    .schedule-list :global(.ap-item) {
        border-left-width: 8px !important;
        margin-bottom: 1px;
        border-top: unset !important;
        border-bottom: unset !important;

        /* box-shadow: unset !important;
    border-radius: unset !important; */
    }
    .schedule-list :global(.ap-item .services) {
        display: flex;
        flex-wrap: wrap;
        column-gap: 10px;
        overflow: hidden;
    }
    .schedule-list :global(.ap-item .services .item) {
        white-space: unset !important;
    }

    .schedule-list :global(.ap-item.agendamento_bloqueado) {
        background: unset !important;
        border: unset !important;
    }

    /* ======================== */
    .selected {
        background-color: #3f51b5 !important;
    }

    .act {
        align-items: center;
        justify-content: center;
        padding: 0px;
        flex-shrink: 0;
        position: absolute;
        right: 1px;
        top: 1px;
        overflow: hidden;
    }

    .act > button {
        width: 26px;
        height: 22px;
        padding: 0px;
        cursor: pointer;
        border: 0px solid #99999999;
        background: #333;
        margin-right: 1px;
        color: white;
    }
    @media (max-width: 900px) {
        /* .list-header {
            position: relative;
            display: none;
        } */

        .schedule-list .-row .-col,
        .schedule-list .-row .-head-col {
            min-width: 340px;
        }
        .schedule-list .-row .-col.-time {
            min-width: unset;
        }
    }

    @media print {
        .list-header {
            padding: 0px 0px;
            position: unset;
            justify-content: center;
            width: 100%;
            display: unset !important;
            font-size: 20px;
        }
    }
</style>
